import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SyncStats } from "@/types/pms";
import { formatRelativeTime } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";

interface SyncEventStatsCardProps {
  stats: SyncStats;
}

export const SyncEventStatsCard = ({ stats }: SyncEventStatsCardProps) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pms.stats.syncEvents.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatItem
            label={t("pms.stats.syncEvents.pending")}
            value={stats.eventStats.pending}
          />
          <StatItem
            label={t("pms.stats.syncEvents.succeeded")}
            value={stats.eventStats.succeeded}
          />
          <StatItem
            label={t("pms.stats.syncEvents.failed")}
            value={stats.eventStats.failed}
            highlight={stats.eventStats.failed > 0}
            variant="destructive"
          />
          <StatItem
            label={t("pms.stats.syncEvents.retrying")}
            value={stats.eventStats.retrying}
            highlight={stats.eventStats.retrying > 0}
            variant="warning"
          />
          <StatItem
            label={t("pms.stats.syncEvents.lastInbound")}
            value={
              formatRelativeTime(stats.lastInboundAt) || t("pms.status.never")
            }
            isText
          />
          <StatItem
            label={t("pms.stats.syncEvents.lastOutbound")}
            value={
              formatRelativeTime(stats.lastOutboundAt) || t("pms.status.never")
            }
            isText
          />
        </div>
      </CardContent>
    </Card>
  );
};

const StatItem = ({
  label,
  value,
  highlight = false,
  variant,
  isText = false,
}: {
  label: string;
  value: number | string;
  highlight?: boolean;
  variant?: "destructive" | "warning";
  isText?: boolean;
}) => (
  <div className="flex flex-col gap-1 rounded-lg border p-4">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span
      className={cn(
        "text-2xl font-semibold",
        isText && "text-base",
        highlight && variant === "destructive" && "text-destructive",
        highlight && variant === "warning" && "text-yellow-600",
      )}
    >
      {value}
    </span>
  </div>
);
